import React, { useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import classNames from 'classnames';
import { Col, Row } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import { faArrowCircleLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import { GLOBAL_PRODUCT_PDP } from 'lib/networking/endpoints';
import CustomContainer from 'components/shared/Container';
import { CUT_DRY_CLIENT_ID } from 'lib/constants';
import FloatingButton from 'components/shared/FloatingButton';
import { GlobalProductsDetailContext } from 'components/global_products/detail_view/GlobalProductsDetailContainer';
import { handleBackNavigation } from 'lib/utils/navigationUtils';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import NutritionSection from 'components/upb_viewer/pdp_components/NutritionSection';
import pointerOnHover from 'lib/css/pointerOnHover';
import ProductDescriptionSection from 'components/upb_viewer/pdp_components/ProductDescriptionSection';
import ProductImagesSection from 'components/upb_viewer/pdp_components/images_section/ProductImagesSection';
import ProductHeaderSection from 'components/upb_viewer/pdp_components/ProductHeaderSection';
import ProductOverviewSection from 'components/upb_viewer/pdp_components/ProductOverviewSection';
import ProductIdentifiersSection from 'components/upb_viewer/pdp_components/ProductIdentifiersSection';
import { ProductType } from 'lib/enums/ProductType';
import PurveyorDetailsSection from 'components/upb_viewer/pdp_components/PurveyorDetailsSection';
import { PFS_ATTRIBUTE_ID, SECTIONS } from 'components/upb_viewer/constants';
import SpecificationsSection from 'components/upb_viewer/pdp_components/SpecificationsSection';
import SupportTicketsCreateModal from 'components/upb_viewer/SupportTicketsCreateModal';
import TaxanomySection from 'components/upb_viewer/pdp_components/TaxanomySection';
import TipsAndHandlingSection from 'components/upb_viewer/pdp_components/TipsAndHandlingSection';
import UniproUpbToggle from 'components/upb_viewer/UniproUpbToggle';
import useGet from 'lib/hooks/useGet';
import WeightAndDimensionsSection from 'components/upb_viewer/pdp_components/WeightAndDimensionsSection';

function UPBProductDetailsPage({
  propProductId,
  productType,
  hideHeader = false,
  customData = null,
}) {
  const { productId: urlProductId, productType: productTypeToGet } = useParams();
  const [showSupportTicketCreateModal, setShowSupportTicketCreateModal] = useState(false);
  const productId = propProductId || urlProductId;
  const history = useHistory();
  const location = useLocation();
  const { setCndProductScore, setP0Score } = useContext(GlobalProductsDetailContext);

  const handleGoBack = () => {
    // Determine fallback based on current location
    let fallback = '/global-products';
    if (location.pathname.includes('upb-pdp')) {
      fallback = '/upb-viewer';
    } else if (location.pathname.includes('product-enrichment-resources')) {
      const pathParts = location.pathname.split('/');
      const resourceIdIndex = pathParts.indexOf('pdf-details') + 1;
      const resourceId = pathParts[resourceIdIndex];
      fallback = resourceId
        ? `/product-enrichment-resources/pdf-qa/${resourceId}`
        : '/product-enrichment-resources';
    }

    handleBackNavigation(history, location, fallback);
  };

  const typeOfProduct = productType || productTypeToGet;
  const isManufacturerProduct = typeOfProduct === ProductType.MANUFACTURER;

  const {
    data: { data: product = {} } = {},
    loading,
    error,
  } = useGet(
    GLOBAL_PRODUCT_PDP,
    { product_id: productId, product_type: typeOfProduct, client_id: CUT_DRY_CLIENT_ID },
    !productId || customData !== null, // Skip API call if customData is provided
  );

  // Use customData if provided, otherwise use API data
  const effectiveProduct = customData || product;

  useEffect(() => {
    if (effectiveProduct && setCndProductScore) {
      setCndProductScore(effectiveProduct.fsa_score);
    }
    if (effectiveProduct && setP0Score) {
      setP0Score(effectiveProduct.p0_score);
    }
    // eslint-disable-next-line
  }, [effectiveProduct]);

  const overviewSectionData = useMemo(
    () => effectiveProduct.sections?.find(section => section.name === SECTIONS.OVERVIEW),
    [effectiveProduct.sections],
  );

  const descriptionSectionData = useMemo(
    () => effectiveProduct.sections?.find(section => section.name === SECTIONS.DESCRIPTION),
    [effectiveProduct.sections],
  );

  const pfsUrl = useMemo(() => {
    const allAttributes = effectiveProduct.sections?.flatMap(section => section.attributes || []);
    return (
      allAttributes?.find(attribute => String(attribute.attribute_id) === PFS_ATTRIBUTE_ID)
        ?.attribute_value || ''
    );
  }, [effectiveProduct.sections]);

  const ingredients = useMemo(() => {
    const ingredientSection = effectiveProduct.sections?.find(
      section => section.name === SECTIONS.INGREDIENTS,
    );
    if (!ingredientSection) {
      return null;
    }
    return ingredientSection.attributes?.find(
      attribute => attribute.attribute_name === 'ingredients_statement',
    )?.attribute_value;
  }, [effectiveProduct.sections]);

  const specifications = useMemo(() => {
    const specificationsSection = effectiveProduct.sections?.find(
      section => section.name === SECTIONS.SPECIFICATIONS,
    );
    if (specificationsSection) {
      return specificationsSection.attributes;
    }
    return [];
  }, [effectiveProduct.sections]);

  const topOff = useMemo(() => {
    const topOffSection = effectiveProduct.sections?.find(
      section => section.name === SECTIONS.TOP_OFF,
    );
    if (topOffSection) {
      return topOffSection.attributes;
    }
    return [];
  }, [effectiveProduct.sections]);

  const weightAndDimensions = useMemo(() => {
    const weightAndDimensionsSection = effectiveProduct.sections?.find(
      section => section.name === SECTIONS.WEIGHTS_AND_DIMENSIONS,
    );
    if (weightAndDimensionsSection) {
      return weightAndDimensionsSection.attributes;
    }
    return [];
  }, [effectiveProduct.sections]);

  const storageInstructions = useMemo(() => {
    const storageInstructionsSection = effectiveProduct.sections?.find(
      section => section.name === SECTIONS.STORAGE_INSTRUCTIONS,
    );
    if (storageInstructionsSection) {
      return storageInstructionsSection.attributes;
    }
    return [];
  }, [effectiveProduct.sections]);

  const nutritionAttributes = useMemo(
    () => effectiveProduct.sections?.find(section => section.name === SECTIONS.NUTRITION_FACTS),
    [effectiveProduct.sections],
  );

  const claims = useMemo(() => {
    const claimsSection = effectiveProduct.sections?.find(
      section => section.name === SECTIONS.CLAIMS,
    );
    if (claimsSection) {
      return claimsSection.attributes.map(attr => attr.attribute_value);
    }
    return [];
  }, [effectiveProduct.sections]);

  const allergens = useMemo(() => {
    const allergensSection = effectiveProduct.sections?.find(
      section => section.name === SECTIONS.ALLERGENS,
    );
    if (allergensSection) {
      return allergensSection.attributes;
    }
    return [];
  }, [effectiveProduct.sections]);

  const tipsAndHandling = useMemo(() => {
    const tipsAndHandlingSection = effectiveProduct.sections?.find(
      section => section.name === SECTIONS.TIPS_AND_HANDLING,
    );
    if (tipsAndHandlingSection) {
      return tipsAndHandlingSection.attributes;
    }
    return '';
  }, [effectiveProduct.sections]);

  const preparationInstructions = useMemo(() => {
    const preparationInstructionsSection = effectiveProduct.sections?.find(
      section => section.name === SECTIONS.PREPARATION_INSTRUCTIONS,
    );
    if (preparationInstructionsSection) {
      return preparationInstructionsSection.attributes;
    }
    return '';
  }, [effectiveProduct.sections]);

  const servingSuggestions = useMemo(() => {
    const servingSuggestionsSection = effectiveProduct.sections?.find(
      section => section.name === SECTIONS.SERVING_SUGGESTIONS,
    );
    if (servingSuggestionsSection) {
      return servingSuggestionsSection.attributes;
    }
    return '';
  }, [effectiveProduct.sections]);

  const productIdentifiers = useMemo(() => {
    const productIdentifiersSection = effectiveProduct.sections?.find(
      section => section.name === SECTIONS.PRODUCT_IDENTIFIERS,
    );
    if (productIdentifiersSection) {
      return productIdentifiersSection.attributes;
    }
    return [];
  }, [effectiveProduct.sections]);

  const childNutritionFacts = useMemo(() => {
    const childNutritionFactsSection = effectiveProduct.sections?.find(
      section => section.name === SECTIONS.CHILD_NUTRITION_FACTS,
    );
    if (childNutritionFactsSection) {
      return childNutritionFactsSection.attributes;
    }
    return [];
  }, [effectiveProduct.sections]);

  const additionalDocuments = useMemo(() => {
    const additionalDocumentsSection = effectiveProduct.sections?.find(
      section => section.name === SECTIONS.ADDITIONAL_DOCUMENTS,
    );
    if (additionalDocumentsSection) {
      return additionalDocumentsSection.attributes;
    }
    return [];
  }, [effectiveProduct.sections]);

  const taxonomies = useMemo(
    () => effectiveProduct.sections?.find(section => section.name === SECTIONS.TAXONOMIES),
    [effectiveProduct.sections],
  );

  if (loading && !customData) {
    return <LoadingSpinner />;
  }

  if ((error || !effectiveProduct) && !customData) {
    return null;
  }

  return (
    <CustomContainer>
      {typeOfProduct !== ProductType.GLOBAL && !customData ? (
        <FloatingButton onClick={() => setShowSupportTicketCreateModal(true)} text="Report issue" />
      ) : null}
      {!hideHeader && !customData && (
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h3 className="font-weight-bold mb-0">
            <FontAwesomeIcon
              className={classNames(pointerOnHover, 'mr-2')}
              icon={faArrowCircleLeft}
              onClick={handleGoBack}
            />
            UPB View
          </h3>
          {isManufacturerProduct && <UniproUpbToggle productId={productId} />}
        </div>
      )}

      <div className="px-5 py-3 container-fluid">
        <Row className={css(styles.detailedContainer)}>
          <Col md={5}>
            <div className={classNames(css(styles.imageContainer), 'pr-5')}>
              <ProductImagesSection images={effectiveProduct.product_images} />
            </div>
          </Col>
          <Col md={7}>
            <div className={classNames(css(styles.contentContainer), 'pl-3')}>
              <ProductHeaderSection
                product={effectiveProduct}
                productType={typeOfProduct}
                showGMVOnPage={location.pathname.includes('upb-pdp')}
                isCustomData={customData !== null}
              />
              <ProductOverviewSection attributes={overviewSectionData?.attributes} />
              <ProductDescriptionSection
                attributes={descriptionSectionData?.attributes}
                additionalDocuments={additionalDocuments}
                pfsUrl={pfsUrl}
              />
              <NutritionSection
                ingredients={ingredients}
                allergens={allergens}
                claims={claims}
                nutritionFacts={nutritionAttributes?.attributes}
                childNutritionFacts={childNutritionFacts}
              />
              <WeightAndDimensionsSection dimensions={weightAndDimensions} />
              <SpecificationsSection specifications={specifications} topOffAttributes={topOff} />
              <TipsAndHandlingSection
                tipsAndHandling={tipsAndHandling}
                storageInstructions={storageInstructions}
                preparationInstructions={preparationInstructions}
                servingSuggestions={servingSuggestions}
              />
              <TaxanomySection taxanomies={taxonomies?.attributes} />
              <ProductIdentifiersSection productIdentifiers={productIdentifiers} />
              <PurveyorDetailsSection
                brand={effectiveProduct?.brand}
                manufacturer={effectiveProduct?.manufacturer}
              />
            </div>
          </Col>
        </Row>
      </div>
      {showSupportTicketCreateModal && (
        <SupportTicketsCreateModal
          onHide={() => setShowSupportTicketCreateModal(false)}
          refetch={() => {}}
          product={effectiveProduct}
          typeOfProduct={typeOfProduct}
        />
      )}
    </CustomContainer>
  );
}

UPBProductDetailsPage.propTypes = {
  propProductId: PropTypes.number,
  productType: PropTypes.string,
  hideHeader: PropTypes.bool,
  customData: PropTypes.object,
};

const styles = StyleSheet.create({
  detailedContainer: {
    display: 'flex',
  },
  imageContainer: {
    flex: '0 0 auto',
    position: 'sticky',
    top: 50,
  },
  contentContainer: {
    background: '#fff',
    minHeight: '50vh',
    flex: 1,
    overflowY: 'auto',
  },
});

export default UPBProductDetailsPage;
